import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, googleProvider, db } from '../firebase';
import {
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const GUEST_USER = {
  uid: 'demo_guest_user',
  email: 'demo@spendeasy.app',
  displayName: 'Guest Demo User',
  photoURL: null,
  isGuest: true
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      if (typeof window !== 'undefined' && localStorage.getItem('spend_easy_guest_user') === 'true') {
        return GUEST_USER;
      }
    } catch (e) {
      console.error('Error checking guest mode:', e);
    }
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [checkingRedirect, setCheckingRedirect] = useState(true);

  const signInAsGuest = () => {
    try {
      localStorage.setItem('spend_easy_guest_user', 'true');
    } catch (e) {}
    setCurrentUser(GUEST_USER);
    setLoading(false);
    return GUEST_USER;
  };

  const signInWithGoogle = async () => {
    try {
      console.log('Starting Google Sign-In with popup...');
      const { signInWithPopup } = await import('firebase/auth');
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      console.log('Google Sign-In successful, user:', user.uid);
      localStorage.removeItem('spend_easy_guest_user');
      await handleRedirectResult(user);
      return user;
    } catch (error) {
      console.error('Popup sign-in error:', error);
      if (error.code === 'auth/popup-blocked' ||
          error.code === 'auth/popup-closed-by-user' ||
          error.code === 'auth/cancelled-popup-request') {
        console.log('Popup blocked or closed, trying redirect method...');
        localStorage.setItem('pendingRedirect', 'true');
        await signInWithRedirect(auth, googleProvider);
      } else {
        throw error;
      }
    }
  };

  const handleRedirectResult = async (user) => {
    if (!user) return;
    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          userId: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          createdAt: serverTimestamp(),
          currency: 'INR',
          defaultAccount: 'Cash'
        });
      }
      localStorage.removeItem('pendingRedirect');
    } catch (error) {
      console.error('Error handling redirect result:', error);
      localStorage.removeItem('pendingRedirect');
    }
  };

  const signOut = async () => {
    try {
      if (currentUser?.isGuest) {
        localStorage.removeItem('spend_easy_guest_user');
        setCurrentUser(null);
        return;
      }
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Sign-out error:', error);
      throw error;
    }
  };

  useEffect(() => {
    const wasPendingRedirect = localStorage.getItem('pendingRedirect') === 'true';
    getRedirectResult(auth)
      .then((result) => {
        if (result && result.user) {
          handleRedirectResult(result.user);
        }
        setCheckingRedirect(false);
      })
      .catch(() => {
        localStorage.removeItem('pendingRedirect');
        setCheckingRedirect(false);
      });

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && wasPendingRedirect) {
        handleRedirectResult(user);
      }
      if (user) {
        localStorage.removeItem('spend_easy_guest_user');
        setCurrentUser(user);
      } else if (localStorage.getItem('spend_easy_guest_user') === 'true') {
        setCurrentUser(GUEST_USER);
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signInWithGoogle,
    signInAsGuest,
    signOut,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
